// The quote's timeline (its `timeline` document) and terms (payment and
// signatures, in its `proposal` document), as the proposal builder had them.

export interface Phase {
  id: string;
  name: string;
  weeks: number;
  description: string;
}

export interface Timeline {
  /** Kickoff, YYYY-MM-DD. */
  start: string;
  phases: Phase[];
}

export interface Milestone {
  id: string;
  name: string;
  /** Share of the one-time price, percent. */
  pct: number;
}

export interface Terms {
  payment: {
    milestones: Milestone[];
    netDays: number;
    billing: 'monthly' | 'annual';
    note: string;
  };
  signatures: {
    customerCompany: string;
    customerName: string;
    customerTitle: string;
    providerCompany: string;
    providerName: string;
    providerTitle: string;
    validDays: number;
    text: string;
  };
}

const id = () => Math.random().toString(36).slice(2, 10);

export const EMPTY_TIMELINE: Timeline = { start: '', phases: [] };

export function defaultTerms(customer: string): Terms {
  return {
    payment: {
      milestones: [
        { id: id(), name: 'At signing', pct: 33.33 },
        { id: id(), name: 'At the start of user testing', pct: 33.33 },
        { id: id(), name: 'At go-live', pct: 33.34 },
      ],
      netDays: 30,
      billing: 'monthly',
      note: '',
    },
    signatures: {
      customerCompany: customer,
      customerName: '',
      customerTitle: '',
      providerCompany: 'Olytic Solutions, LLC',
      providerName: '',
      providerTitle: '',
      validDays: 30,
      text: 'By signing, the parties accept this proposal, including its pricing and payment terms.',
    },
  };
}

export function readTimeline(stored: unknown): Timeline {
  const t = stored && typeof stored === 'object' ? (stored as Partial<Timeline>) : {};
  return { ...EMPTY_TIMELINE, ...t, phases: Array.isArray(t.phases) ? t.phases : [] };
}

export function readTerms(proposal: unknown, customer: string): Terms {
  const stored = proposal && typeof proposal === 'object' ? ((proposal as { terms?: Partial<Terms> }).terms ?? {}) : {};
  const base = defaultTerms(customer);
  return { payment: { ...base.payment, ...stored.payment }, signatures: { ...base.signatures, ...stored.signatures } };
}

export const totalWeeks = (t: Timeline) => t.phases.reduce((total, p) => total + (Number(p.weeks) || 0), 0);

/**
 * Months from kickoff to launch: every phase but the last (the last is the
 * settling-in after go-live), at 4.345 weeks a month, as the prototype did.
 * Null without at least two phases.
 */
export function goLiveMonthsOf(t: Timeline): number | null {
  if (t.phases.length < 2) return null;
  return t.phases.slice(0, -1).reduce((total, p) => total + (Number(p.weeks) || 0), 0) / 4.345;
}

/** The date `weeks` after `start` (YYYY-MM-DD), or null without a start. */
export function addWeeks(start: string, weeks: number): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start)) return null;
  const d = new Date(`${start}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + Math.round(weeks * 7));
  return d.toISOString().slice(0, 10);
}

/** Each phase with its start and end dates, and go-live (the end of the next-to-last phase). */
export function scheduleOf(t: Timeline) {
  let elapsed = 0;
  const phases = t.phases.map((p) => {
    const from = addWeeks(t.start, elapsed);
    elapsed += Number(p.weeks) || 0;
    return { ...p, from, to: addWeeks(t.start, elapsed) };
  });
  const goLive = t.phases.length >= 2 ? phases[phases.length - 2]!.to : (phases.at(-1)?.to ?? null);
  return { phases, goLive, weeks: elapsed };
}

/** Each milestone's amount of `oneTime`, and whether the shares add to 100%. */
export function milestoneAmounts(terms: Terms, oneTime: number) {
  const total = terms.payment.milestones.reduce((sum, m) => sum + (Number(m.pct) || 0), 0);
  return {
    rows: terms.payment.milestones.map((m) => ({ ...m, amount: Math.round((oneTime * (Number(m.pct) || 0)) / 100) })),
    totalPct: Math.round(total * 100) / 100,
    balanced: Math.abs(total - 100) < 0.05,
  };
}
